import { Layout } from '../components/Layout';
import { 
  ShoppingBag, 
  Package, 
  Sparkles, 
  Users, 
  Shield, 
  MessageCircle,
  MapPin,
  CheckCircle,
  HelpCircle,
  ChevronDown,
  Leaf,
  Heart,
  Camera,
  Globe,
  Handshake,
  X
} from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const FAQ_ITEMS = [
  {
    question: "What is WANTD?",
    answer: "WANTD is a local marketplace where you can browse items for sale near you, or post what you're looking for and let sellers come to you. Our intelligent matching system connects buyers with sellers in their area."
  },
  {
    question: "How is this different from Facebook Marketplace or Gumtree?",
    answer: "WANTD offers both traditional browsing AND the ability to post what you want. You can browse the catalog like any marketplace, or post a 'want' with your price range and let sellers be notified when they have a match. It's like having a personal shopper working 24/7."
  },
  {
    question: "Is it free to use WANTD?",
    answer: "WANTD is free to use. Creating an account, posting Wants, listing items, and connecting with buyers or sellers costs nothing. Transactions happen offline between buyers and sellers."
  },
  {
    question: "How does the matching system work?",
    answer: "Our intelligent matching algorithm considers multiple factors: category match, price overlap (your budget vs seller's asking price), location proximity, item condition preferences, and trust scores. When a strong match is found, both parties are notified."
  },
  {
    question: "What is the Trust Score?",
    answer: "Your Trust Score reflects your reliability on the platform. You earn points by verifying your account, maintaining active and accurate listings, and engaging positively with other users. Higher trust scores make your listings more visible and increase match rates."
  },
  {
    question: "How do I meet up with buyers/sellers?",
    answer: "After unlocking a match, you can chat directly with the other party to arrange meeting details. We recommend meeting in public places during daylight hours. Always verify the item before completing the deal."
  },
  {
    question: "Is my personal information safe?",
    answer: "Yes. Your exact location is never shared - only the suburb/area name. Personal contact details are only revealed after both parties agree to unlock a match. We use industry-standard encryption for all data."
  },
  {
    question: "What if a seller doesn't show up or the item isn't as described?",
    answer: "We take platform integrity seriously. Users who repeatedly fail to complete matches or misrepresent items will have their trust scores reduced and may be suspended. Always check items thoroughly before completing the deal."
  }
];

export default function HowItWorksPage() {
  const [openFaq, setOpenFaq] = useState(null);
  const navigate = useNavigate();

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-8 relative">
        {/* Close Button */}
        <button
          onClick={() => navigate('/')}
          className="absolute top-4 right-4 md:top-8 md:right-6 w-10 h-10 rounded-full bg-[var(--theme-card)] border border-[var(--theme-border)] flex items-center justify-center hover:bg-[var(--theme-hover)] transition-colors shadow-sm z-10"
          title="Back to Home"
        >
          <X className="w-5 h-5 text-[var(--theme-text-secondary)]" />
        </button>

        {/* Hero Section */}
        <section className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-black text-[var(--theme-text-primary)] mb-4">
            How WANTD Works
          </h1>
          <p className="text-lg text-[var(--theme-text-secondary)] max-w-2xl mx-auto">
            Buy, sell, and discover locally. Browse items for sale or post what you're looking for.
          </p>
        </section>

        {/* Community Message */}
        <section className="mb-16 bg-gradient-to-br from-[#1E3A5F]/5 to-[#C9956C]/5 rounded-2xl border border-[var(--theme-border)] p-8 md:p-10">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#C9956C]/20 flex items-center justify-center flex-shrink-0">
              <Heart className="w-6 h-6 text-[#C9956C]" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-3">Support Your Community</h2>
              <p className="text-[var(--theme-text-secondary)] leading-relaxed">
                Have an old hammer lying around? Someone might need that! Support your local community 
                and prevent global wastage by passing on your used goods to those who may need or want them. 
                If you're feeling charitable, you can mark an item as free, and it will match with anyone 
                looking for it - however you might find yourself making some spare change for that old hammer.
              </p>
            </div>
          </div>
        </section>

        {/* How It Works Steps - Buyers */}
        <section className="mb-20">
          <h2 className="text-2xl font-bold text-[var(--theme-text-primary)] text-center mb-12">
            For Buyers (Wanters)
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-[#C9956C]/10 flex items-center justify-center mx-auto mb-4">
                <ShoppingBag className="w-8 h-8 text-[#C9956C]" />
              </div>
              <div className="text-sm font-bold text-[#C9956C] mb-2">STEP 1</div>
              <h3 className="text-xl font-bold text-[var(--theme-text-primary)] mb-2">Post What You Want</h3>
              <p className="text-[var(--theme-text-secondary)]">
                Create a "Want to Buy" ad with your desired item, price range, and how flexible you are on specs.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-[var(--theme-hover)] flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-[var(--theme-text-primary)]" />
              </div>
              <div className="text-sm font-bold text-[var(--theme-text-primary)] mb-2">STEP 2</div>
              <h3 className="text-xl font-bold text-[var(--theme-text-primary)] mb-2">Get Matched</h3>
              <p className="text-[var(--theme-text-secondary)]">
                Our AI matches your want with nearby sellers' inventory. You'll be notified when matches are found.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-[#059669]/10 flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-[#059669]" />
              </div>
              <div className="text-sm font-bold text-[#059669] mb-2">STEP 3</div>
              <h3 className="text-xl font-bold text-[var(--theme-text-primary)] mb-2">Connect & Deal</h3>
              <p className="text-[var(--theme-text-secondary)]">
                Unlock the match to chat with the seller, negotiate, and meet up to complete the transaction.
              </p>
            </div>
          </div>
        </section>

        {/* How It Works Steps - Sellers */}
        <section className="mb-20">
          <h2 className="text-2xl font-bold text-[var(--theme-text-primary)] text-center mb-12">
            For Sellers (Owners)
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-[var(--theme-hover)] flex items-center justify-center mx-auto mb-4">
                <Package className="w-8 h-8 text-[var(--theme-text-primary)]" />
              </div>
              <div className="text-sm font-bold text-[var(--theme-text-primary)] mb-2">STEP 1</div>
              <h3 className="text-xl font-bold text-[var(--theme-text-primary)] mb-2">List Your Items</h3>
              <p className="text-[var(--theme-text-secondary)]">
                List items you'd consider selling. No commitment required - only sell when you find the right buyer.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-[#C9956C]/10 flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-8 h-8 text-[#C9956C]" />
              </div>
              <div className="text-sm font-bold text-[#C9956C] mb-2">STEP 2</div>
              <h3 className="text-xl font-bold text-[var(--theme-text-primary)] mb-2">Get Buyer Requests</h3>
              <p className="text-[var(--theme-text-secondary)]">
                When a buyer's want matches your item, you'll be notified. Review the match and decide if you're interested.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-[#059669]/10 flex items-center justify-center mx-auto mb-4">
                <Handshake className="w-8 h-8 text-[#059669]" />
              </div>
              <div className="text-sm font-bold text-[#059669] mb-2">STEP 3</div>
              <h3 className="text-xl font-bold text-[var(--theme-text-primary)] mb-2">Sell with Zero Fees</h3>
              <p className="text-[var(--theme-text-secondary)]">
                Connect with the buyer, arrange the meetup, and complete the deal. WANTD is completely free to use!
              </p>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section className="mb-20" id="about">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-[var(--theme-text-primary)] mb-4">About WANTD</h2>
            <div className="w-16 h-1 bg-[#C9956C] mx-auto rounded-full"></div>
          </div>

          <div className="bg-[var(--theme-card)] rounded-2xl border border-[var(--theme-border)] overflow-hidden">
            {/* Our Vision */}
            <div className="p-8 md:p-10 border-b border-[var(--theme-border)]">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#059669]/10 flex items-center justify-center flex-shrink-0">
                  <Leaf className="w-6 h-6 text-[#059669]" />
                </div>
                <h3 className="text-xl font-bold text-[var(--theme-text-primary)]">Our Vision</h3>
              </div>
              <div className="space-y-4 text-[var(--theme-text-secondary)] leading-relaxed">
                <p>
                  We share a concern that modern consumerism is becoming unsustainable. Instead of asking 
                  a neighbour for a jerry can, we tend to go and buy a new one. Globally, this mentality 
                  creates an unhealthy demand for the perpetual production of things that our communities 
                  don't actually need.
                </p>
                <p>
                  Gone are the days of knocking on your neighbour's door to see if they have a spare ladder 
                  they don't use - WANTD allows you to create that demand in your vicinity, and automatically 
                  source and find a "neighbour" that may have one lying around. Our vision is to reduce global 
                  production rates of new commodity products by promoting the transaction of perfectly good, 
                  used goods within a practical locale.
                </p>
                <p>
                  No need to pay for postage, no need to wait a week for the shared shipping container to arrive. 
                  It's likely that someone near you has something that you want, that they just haven't thought of selling.
                </p>
              </div>
            </div>

            {/* AI Inventory */}
            <div className="p-8 md:p-10 border-b border-[var(--theme-border)] bg-[var(--theme-hover)]">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[var(--theme-card)] flex items-center justify-center flex-shrink-0">
                  <Camera className="w-6 h-6 text-[var(--theme-text-primary)]" />
                </div>
                <h3 className="text-xl font-bold text-[var(--theme-text-primary)]">AI-Powered Inventory</h3>
              </div>
              <div className="space-y-4 text-[var(--theme-text-secondary)] leading-relaxed">
                <p>
                  Our AI powered inventory system makes it completely frictionless to add items to your personal 
                  inventory, ready to be matched with someone wanting it. Simply take one or more photos of items 
                  in your rooms, cupboards, living rooms, garages and sheds, and our image analysis tool detects 
                  the items in each photo, adds a description and suggests an inventory item for you to add.
                </p>
                <p>
                  Don't want to sell your dining table? No problem, just reject the suggestion. The analysis tool 
                  can detect and match known products to brands, models, and product names, or if the item is unique 
                  or unbranded, it produces a descriptive summary that makes it suitable for matching to WANTD ads.
                </p>
              </div>
            </div>

            {/* AI Matching */}
            <div className="p-8 md:p-10 border-b border-[var(--theme-border)]">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#C9956C]/10 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-6 h-6 text-[#C9956C]" />
                </div>
                <h3 className="text-xl font-bold text-[var(--theme-text-primary)]">Intelligent Matching</h3>
              </div>
              <p className="text-[var(--theme-text-secondary)] leading-relaxed">
                Our AI matching system automatically connects buyers with your listed items. 
                You may receive a suggested match within seconds of uploading a photo and approving your 
                listings.
              </p>
            </div>

            {/* Trust & Integrity */}
            <div className="p-8 md:p-10 border-b border-[var(--theme-border)] bg-[var(--theme-hover)]">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#059669]/10 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-[#059669]" />
                </div>
                <h3 className="text-xl font-bold text-[var(--theme-text-primary)]">Trust & Integrity</h3>
              </div>
              <div className="space-y-4 text-[var(--theme-text-secondary)] leading-relaxed">
                <p>
                  Every user has a trust score, built through verified activity, accurate listings, and positive
                  interactions with other users. We want to make sure that every WANTD listing is honest, valid
                  and true, and our trust system helps maintain platform integrity.
                </p>
              </div>
            </div>

            {/* Mission Statement */}
            <div className="p-8 md:p-10 bg-gradient-to-br from-[#1E3A5F] to-[#2D4A6F] text-white">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Handshake className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold">Our Mission</h3>
              </div>
              <p className="text-white/90 leading-relaxed text-lg">
                At WANTD, we want you, your neighbours and your broader community to engage and transact with 
                each other before buying new consumer items. We hope it will save you money, reduce unnecessary 
                consumption and bring our communities closer together.
              </p>
            </div>
          </div>
        </section>

        {/* Trust & Safety */}
        <section className="mb-20 bg-[var(--theme-card)] rounded-2xl border border-[var(--theme-border)] p-8 md:p-12">
          <h2 className="text-2xl font-bold text-[var(--theme-text-primary)] text-center mb-8">
            Trust & Safety
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <Shield className="w-6 h-6 text-[var(--theme-text-primary)]" />
              </div>
              <div>
                <h3 className="font-bold text-[var(--theme-text-primary)] mb-1">Trust Scores</h3>
                <p className="text-sm text-[var(--theme-text-secondary)]">
                  Every user has a trust score built through verified activity and positive interactions.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <CheckCircle className="w-6 h-6 text-[#059669]" />
              </div>
              <div>
                <h3 className="font-bold text-[var(--theme-text-primary)] mb-1">Verified Users</h3>
                <p className="text-sm text-[var(--theme-text-secondary)]">
                  Verify your account to build trust and get priority matching with other users.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <MapPin className="w-6 h-6 text-[#C9956C]" />
              </div>
              <div>
                <h3 className="font-bold text-[var(--theme-text-primary)] mb-1">Location Privacy</h3>
                <p className="text-sm text-[var(--theme-text-secondary)]">
                  Your exact location is never shared. Only suburb/area names are shown until you choose to share.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <MessageCircle className="w-6 h-6 text-[var(--theme-text-primary)]" />
              </div>
              <div>
                <h3 className="font-bold text-[var(--theme-text-primary)] mb-1">In-App Messaging</h3>
                <p className="text-sm text-[var(--theme-text-secondary)]">
                  All communication happens within WANTD. No need to share personal phone numbers or emails.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-[var(--theme-text-primary)] text-center mb-8">
            <HelpCircle className="w-6 h-6 inline mr-2" />
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-3">
            {FAQ_ITEMS.map((item, index) => (
              <div 
                key={index}
                className="bg-[var(--theme-card)] rounded-xl border border-[var(--theme-border)] overflow-hidden"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-[var(--theme-hover)] transition-colors"
                  data-testid={`faq-item-${index}`}
                >
                  <span className="font-medium text-[var(--theme-text-primary)]">{item.question}</span>
                  <ChevronDown 
                    className={`w-5 h-5 text-[var(--theme-text-secondary)] transition-transform ${openFaq === index ? 'rotate-180' : ''}`}
                  />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4">
                    <p className="text-[var(--theme-text-secondary)] leading-relaxed">{item.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center bg-[#1E3A5F] rounded-2xl p-8 md:p-12">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-white/70 mb-6 max-w-xl mx-auto">
            Join thousands of Australians using WANTD to find exactly what they're looking for.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/register" 
              className="inline-flex items-center justify-center px-8 py-3 bg-[#C9956C] hover:bg-[#B8845B] text-white font-semibold rounded-full transition-colors"
            >
              Create Free Account
            </Link>
            <Link 
              to="/" 
              className="inline-flex items-center justify-center px-8 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-full transition-colors border border-white/20"
            >
              Browse Listings
            </Link>
          </div>
        </section>
      </div>
    </Layout>
  );
}
